import type {
  AssistantMessage,
  AssistantMessageDelta,
  ChatMessage,
  MessageResponse,
  StepType,
  StreamingAssistantMessage,
  WebSearchResult,
} from "../Components/ChatPanel/ChatThread/ChatMessage/messageTypes";
import { parseHttpUrl } from "../../utils";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const WEB_SEARCH_STEP_TYPE: StepType = "web_search_used";

/**
 * Validates `web_search_results`, keeping every entry with a safe http(s) url,
 * in order. Urls are normalized to their `href` and deduped on it.
 */
export const parseWebSearchResults = (results: unknown): WebSearchResult[] => {
  if (!Array.isArray(results)) {
    return [];
  }
  const validResults: WebSearchResult[] = [];
  const seenUrls = new Set<string>();
  for (const result of results) {
    if (!isRecord(result)) {
      continue;
    }
    const { title, url, snippet } = result;
    const href = typeof url === "string" ? parseHttpUrl(url)?.href : undefined;
    if (!href || seenUrls.has(href)) {
      continue;
    }
    seenUrls.add(href);
    validResults.push({
      url: href,
      title: typeof title === "string" ? title : "",
      snippet: typeof snippet === "string" ? snippet : "",
    });
  }
  return validResults;
};

const createEmptyStreamingAssistantMessage = (): StreamingAssistantMessage => ({
  reasoning: [],
  answers: [],
  // No uuid until the stream's `done` event reports the persisted id.
  message_uuid: "",
});

// Shared by live deltas and history rows so both route the step identically.
// A turn can search more than once, so each step's results are merged onto the
// earlier ones. Returns null for any other step type.
const getWebSearchStepResults = (
  {
    step_type,
    web_search_results,
  }: Pick<MessageResponse, "step_type" | "web_search_results">,
  previousResults: WebSearchResult[] | undefined
): WebSearchResult[] | null => {
  if (step_type !== WEB_SEARCH_STEP_TYPE) {
    return null;
  }
  const newResults = Array.isArray(web_search_results)
    ? web_search_results
    : [];
  return parseWebSearchResults([...(previousResults ?? []), ...newResults]);
};

export const normalizeChatHistory = (
  messages: MessageResponse[]
): ChatMessage[] => {
  return messages.reduce((acc: ChatMessage[], message: MessageResponse) => {
    if (message.role === "user") {
      acc.push({
        role: message.role,
        content: message.content,
        contentType: message.content_type,
        message_uuid: message.message_uuid,
        isCurrentStream: false,
      });
    } else if (message.role === "assistant") {
      let currentAssistantMessage = acc.find(
        (m: ChatMessage) => m.message_uuid === message.message_uuid
      );
      if (!currentAssistantMessage) {
        currentAssistantMessage = {
          role: message.role,
          reasoning: [],
          answers: [],
          message_uuid: message.message_uuid,
          isCurrentStream: false,
        };
        acc.push(currentAssistantMessage);
      }

      const webSearchResults = getWebSearchStepResults(
        message,
        (currentAssistantMessage as AssistantMessage).webSearchResults
      );
      if (webSearchResults) {
        if (webSearchResults.length) {
          (currentAssistantMessage as AssistantMessage).webSearchResults =
            webSearchResults;
        }
      } else if (
        message.step_type === "reasoning" ||
        message.step_type === "tool_execution"
      ) {
        (currentAssistantMessage as AssistantMessage).reasoning.push({
          type: message.step_type!,
          content: message.content,
        });
      } else {
        (currentAssistantMessage as AssistantMessage).answers.push({
          content: message.content,
          contentType: message.content_type,
          stepType: message.step_type!,
          isFinal: !!message.is_final,
        });
      }
    }

    return acc;
  }, []);
};

export const buildAssistantMessage = (
  assistantMessageDelta: AssistantMessageDelta,
  currentAssistantMessage: StreamingAssistantMessage | null
): StreamingAssistantMessage => {
  const localAssistantMessage: StreamingAssistantMessage =
    !!currentAssistantMessage
      ? {
          ...currentAssistantMessage,
          reasoning: [...(currentAssistantMessage.reasoning ?? [])],
          answers: [...(currentAssistantMessage.answers ?? [])],
        }
      : createEmptyStreamingAssistantMessage();
  const webSearchResults = getWebSearchStepResults(
    assistantMessageDelta,
    localAssistantMessage.webSearchResults
  );
  if (webSearchResults) {
    if (webSearchResults.length) {
      localAssistantMessage.webSearchResults = webSearchResults;
    }
  } else if (
    assistantMessageDelta.step_type === "reasoning" ||
    assistantMessageDelta.step_type === "tool_execution"
  ) {
    localAssistantMessage.reasoning?.push({
      type: assistantMessageDelta.step_type!,
      content: assistantMessageDelta.content,
    });
  } else {
    localAssistantMessage.answers?.push({
      content: assistantMessageDelta.content,
      stepType: assistantMessageDelta.step_type!,
      contentType: assistantMessageDelta.content_type,
      isFinal: !!assistantMessageDelta.is_final,
    });
  }
  return localAssistantMessage;
};
